import { Button, MenuItem, Select, FormHelperText, CircularProgress } from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import * as Yup from 'yup';

import { showError } from '../../constants/toasts';
import BikeForm from '../../features/vehicleForm/bikeForm';
import CarForm from '../../features/vehicleForm/carForm';
import { errorToast, Loader, successToast } from '../../helpers';
import MainContainer from '../../layout/MainContainer';
import { useGetMasterDataQuery, useLazyGetMasterDataQuery } from '../../service/common';
import {
  useAddListingMutation,
  useLazyGetListingByIdQuery,
  useUpdateListingDetailsMutation,
} from '../../service/listing';
import { CommonBody, DamageData, ListingById, MasterData, MasterUser } from '../../types/General';
import { generateEncryptedKeyBody } from '../../utils/crypto';
import { MASTER_DATA, USER_TYPE, VEHICLE_TYPE } from '../../utils/enums';

type Image_info = {
  make: string;
  model: string;
  color: string;
};

const AddVehicle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [masterData, setMasterData] = useState<MasterData>();

  const [userData, setUserData] = useState<MasterUser[]>([]);
  const [details, setDetails] = useState<ListingById>();
  const [selectedVehicleType, setSelectedVehicleType] = useState<number>();
  const [fleetDamage, setFleetDamage] = useState<any>([]);
  const [features, setFeatures] = useState<any>([]);
  const [accidentHistory, setAccidentHistory] = useState(true);
  const [carImages, setCarImages] = useState<string[]>([]);
  const [coverImage, setCoverImage] = useState('');
  const [imageInfo, setImageInfo] = useState<Image_info>({
    color: '',
    make: '',
    model: '',
  });

  const [addListing, { isLoading }] = useAddListingMutation();
  const [updateListing, { isLoading: updateLoading }] = useUpdateListingDetailsMutation();
  const [getMasterData] = useLazyGetMasterDataQuery({});
  const [getDetail] = useLazyGetListingByIdQuery();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      vehicleType: details?.vehicle?._id || 'default',
      userType: details?.seller?.role || 'default',
      selectedUser: details?.seller?._id || 'default',
      //
      title: details?.title || '',
      brandId: details?.brand?._id || 'default',
      modelId: details?.model?._id || 'default',
      bodyTypeId: details?.bodyType?._id || 'default',
      mileage: details?.mileage || '',
      gccId: details?.gcc?._id || 'default',
      fuelTypeId: details?.fuelType?._id || 'default',
      seat: details?.seat || 'default',
      door: details?.door || 'default',
      transmission: details?.transmission || 'default',
      horsePowerId: details?.horsePower?._id || 'default',
      engineCapacityId: details?.engineCapacity?._id || 'default',
      interior: details?.color?.interiorDetails?._id || 'default',
      exterior: details?.color?.exteriorColor?._id || 'default',
      price: details?.price || '',
      address: details?.addressObject?.address || '',
      city: details?.addressObject?.city || '',
      description: details?.description || '',
      accidentHistoryDesc: details?.accidentHistoryDesc || '',
      condition: details?.condition || 'default',
      steeringSide: details?.steeringSide || 'default',
      bikeDescription: '',

      image: '',

      coverImage: '',
      vehicleId: '',
      damageId: '',

      featureId: '',
      wheel: details?.wheel || '',
      numberofCylinder: details?.NumberOfCylinder || '',
    },

    validationSchema: Yup.object({
      vehicleType: Yup.string().notOneOf(['default'], 'Vehicle Type is required'),
      userType: Yup.string().notOneOf(['default'], 'User Type is required'),
      selectedUser: Yup.string().when('userType', {
        is: (val: string) => val && val !== 'default',
        then: (schema: { notOneOf: (arg0: string[], arg1: string) => any }) =>
          schema.notOneOf(['default'], 'User selection is required'),
      }),
      title: Yup.string().required('Title is required'),
      brandId: Yup.string().notOneOf(['default'], 'Brand is required'),
      modelId: Yup.string().notOneOf(['default'], 'Model is required'),
      bodyTypeId: Yup.string().notOneOf(['default'], 'Body Type is required'),
      mileage: Yup.string().required('Mileage is required'),
      gccId: Yup.string().notOneOf(['default'], 'GCC is required'),
      fuelTypeId: Yup.string().notOneOf(['default'], 'Fuel type is required'),
      seat: Yup.string().notOneOf(['default'], 'This field is required'),
      door: Yup.string().notOneOf(['default'], 'This field is required'),
      transmission: Yup.string().notOneOf(['default'], 'Transmission type is required'),
      horsePowerId: Yup.string().notOneOf(['default'], 'This field is required'),
      engineCapacityId: Yup.string().notOneOf(['default'], 'This field is required'),
      interior: Yup.string().notOneOf(['default'], 'This field is required'),
      exterior: Yup.string().notOneOf(['default'], 'This field is required'),
      price: Yup.string().required('Price is required'),
      address: Yup.string().required('Area is required'),
      city: Yup.string().required('City is required'),
      description: Yup.string().required('Description is required'),
      condition: Yup.string().notOneOf(['default'], 'This field is required'),
      steeringSide: Yup.string().notOneOf(['default'], 'This field is required'),
      wheel: Yup.string().required('Number of wheels are required'),
      numberofCylinder: Yup.string().required('Number of cylinders are required'),
    }),

    onSubmit: async (values: any) => {
      try {
        if (accidentHistory && !values.accidentHistoryDesc) {
          errorToast('Please provide accident history description');
          return;
        }
        if (!coverImage) {
          errorToast('Please provide cover image');
          return;
        }
        let body;
        if (selectedVehicleType === VEHICLE_TYPE.CAR) {
          body = {
            wheel: values.wheel,
            NumberOfCylinder: values.numberofCylinder,
            door: values.door,
            seat: values.seat,
            description: values.description,
            price: values.price,
            mileage: values.mileage,
            title: values.title,
            steeringSide: values.steeringSide,
            image: carImages,
            condition: values.condition,
            transmission: values.transmission,
            coverImage: coverImage,
            vehicleId: values.vehicleType,
            damageId: fleetDamage?.map((item: DamageData) => item._id) || [],
            brandId: values.brandId,
            modelId: values.modelId,
            bodyTypeId: values.bodyTypeId,
            fuelTypeId: values.fuelTypeId,
            gccId: values.gccId,
            horsePowerId: values.horsePowerId,
            engineCapacityId: values.engineCapacityId,
            featureId: features?.map((item: DamageData) => item._id) || [],
            color: {
              interior: values.interior,
              exterior: values.exterior,
            },
            isAccidentHistory: accidentHistory,
            accidentHistoryDesc: values.accidentHistoryDesc,
            isInWarranty: true,
            addressObject: {
              address: values.address,
              city: values.city,
              // country: 'Mohali',
              // pincode: 'Mohali',
              // state: 'Mohali',
            },
            latitude: 0,
            longitude: 0,
            sellerId: values.selectedUser,
          };
        }
        // return;
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        let response;
        if (id) {
          response = await updateListing({
            body: encryptedBody,
            id: id,
          }).unwrap();
        } else {
          response = await addListing({ body: encryptedBody }).unwrap();
        }
        if (response?.statusCode === 200) {
          successToast(id ? 'Vehicle updated successfully' : 'Vehicle added successfully');
          navigate('/listings/individual');
        }
      } catch (error: any) {
        showError(error?.data?.message || 'Something went wrong');
        console.error(error);
      }
    },
  });

  const bikeFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      vehicleType: 'default',
      userType: 'default',
      selectedUser: 'default',
      title: '',
      brandId: 'default',
      modelId: 'default',
      bodyTypeId: 'default',
      mileage: '',
      gccId: 'default',
      fuelTypeId: 'default',
      seat: 'default',
      door: 'default',
      transmission: 'default',
      horsePowerId: 'default',
      engineCapacityId: 'default',
      interior: 'default',
      exterior: 'default',
      price: '',
      address: '',
      city: '',
      description: '',
      accidentHistoryDesc: '',
      condition: 'default',
      steeringSide: 'default',
      image: '',
      coverImage: '',
      vehicleId: '',
      damageId: '',

      featureId: '',
      wheel: '',
      numberofCylinder: '',
    },
    onSubmit: async () => {},
  });
  const searchParams = [
    MASTER_DATA.VEHICLE_TYPE,
    MASTER_DATA.BODY_TYPE,
    MASTER_DATA.FEATURE,
    MASTER_DATA.BRAND,
    MASTER_DATA.COLOR,
    MASTER_DATA.DAMAGE,
    MASTER_DATA.ENGINE_CAPACITY,
    MASTER_DATA.EQUIPMENT,
    MASTER_DATA.DRIVE_SYSTEM,
    MASTER_DATA.FUEL_TYPE, // Removed duplicate EQUIPMENT
    MASTER_DATA.GCC,
    MASTER_DATA.HORSE_POWER,
    MASTER_DATA.VEHICLE_MODEL,
  ].join(',');

  const { data, isSuccess } = useGetMasterDataQuery({
    search: searchParams,
    ...(formik.values.userType !== 'default' ? { role: Number(formik.values?.userType) } : {}),
    ...(formik.values.vehicleType !== 'default' ? { vehicleId: formik.values.vehicleType } : {}),
  });

  // const fetchMasterData = async () => {
  //   const searchParams = [
  //     MASTER_DATA.VEHICLE_TYPE,
  //     MASTER_DATA.BODY_TYPE,
  //     MASTER_DATA.FEATURE,
  //     MASTER_DATA.BRAND,
  //     MASTER_DATA.COLOR,
  //     MASTER_DATA.DAMAGE,
  //     MASTER_DATA.ENGINE_CAPACITY,
  //     MASTER_DATA.EQUIPMENT,
  //     MASTER_DATA.DRIVE_SYSTEM,
  //     MASTER_DATA.FUEL_TYPE, // Removed duplicate EQUIPMENT
  //     MASTER_DATA.GCC,
  //     MASTER_DATA.HORSE_POWER,
  //     MASTER_DATA.VEHICLE_MODEL,
  //   ].join(',');
  //   setLoading(true);
  //   try {
  //     const res = await getMasterData({
  //       search: searchParams,
  //       ...(formik.values.userType !== 'default' ? { role: Number(formik.values?.userType) } : {}),
  //       ...(formik.values.vehicleType !== 'default'
  //         ? { vehicleId: formik.values.vehicleType }
  //         : {}),
  //     }).unwrap();
  //     if (res?.statusCode === 200) {
  //       setMasterData(res?.data);
  //       setLoading(false);
  //     }
  //   } catch (error: any) {
  //     console.error(error);
  //     setLoading(false);
  //   }
  // };

  const fetchUserListing = async (userType: any) => {
    const searchParams = [MASTER_DATA.USER_TYPE].join(',');
    setLoading(true);
    try {
      const res = await getMasterData({
        search: searchParams,
        role: Number(userType),
      }).unwrap();
      if (res?.statusCode === 200) {
        setUserData(res?.data?.user || []);
        setLoading(false);
      }
    } catch (error: any) {
      console.error(error);
      setLoading(false);
    }
  };

  const getListingDetail = async (id: string) => {
    setLoading(true);
    try {
      const response = await getDetail({
        id,
      }).unwrap();

      if (response?.statusCode === 200) {
        setDetails(response?.data);
        if (response?.data?.vehicle?.vehicleType) {
          setSelectedVehicleType(Number(response?.data?.vehicle?.vehicleType));
        }
        setCarImages(response?.data?.image || []);
        setCoverImage(response?.data?.coverImage || '');
        setAccidentHistory(response?.data?.isAccidentHistory || false);
        setFleetDamage(response?.data?.damages || []);
        setFeatures(response?.data?.features || []);
        setLoading(false);
      }
    } catch (error: any) {
      showError(error?.data?.message || '');
      setLoading(false);
    }
  };
  // useEffect(() => {
  //   fetchMasterData();
  // }, [selectedVehicleType, formik.values.vehicleType]);

  useEffect(() => {
    if (formik?.values?.userType !== 'default') fetchUserListing(formik.values.userType);
  }, [formik.values.userType]);

  useEffect(() => {
    if (id) {
      getListingDetail(id);
    }
  }, [id]);

  useEffect(() => {
    if (isSuccess) {
      setMasterData(data?.data);
    }
  }, [isSuccess, data]);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title fdx">
        <div>
          <h1>{id ? 'Edit' : 'Add'} Vehicle</h1>
          <p>
            <span onClick={() => navigate('/dashboard')}>Dashboard</span> -{' '}
            <span onClick={() => navigate('/listings/myList')}>My Listings</span> -{' '}
            {id ? 'Edit' : 'Add'} Vehicle
          </p>
        </div>
        <div className="right_s align_right">
          <Button className="btn btn_orange" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Select Vehicle Type</label>
              <Select
                fullWidth
                id="vehicleType"
                name="vehicleType"
                value={masterData?.vehicleType?.length ? formik.values.vehicleType : ''}
                onChange={(event) => {
                  const selectedItem = masterData?.vehicleType?.find(
                    (item) => item?._id === event?.target?.value
                  );
                  if (selectedItem) {
                    formik.setFieldValue('vehicleType', selectedItem?._id);
                    setSelectedVehicleType(selectedItem?.vehicleType);
                  }
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.vehicleType && Boolean(formik.errors.vehicleType)}
              >
                <MenuItem disabled value="default">
                  Select Vehicle Type
                </MenuItem>
                {masterData?.vehicleType?.length
                  ? masterData?.vehicleType?.map((item) => {
                      return (
                        <MenuItem
                          key={masterData?.vehicleType?.length ? item?._id : ''}
                          value={masterData?.vehicleType?.length ? item?._id : ''}
                        >
                          {item?.name || ''}
                        </MenuItem>
                      );
                    })
                  : null}
              </Select>
              <FormHelperText error>
                {formik.touched.vehicleType && typeof formik.errors.vehicleType === 'string'
                  ? formik.errors.vehicleType
                  : ''}
              </FormHelperText>
            </div>

            <div className="control_group w_50">
              <label>Select User Type</label>
              <Select
                fullWidth
                id="userType"
                name="userType"
                value={formik.values.userType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.userType && Boolean(formik.errors.userType)}
              >
                <MenuItem disabled value="default">
                  Select User Type
                </MenuItem>
                <MenuItem value={USER_TYPE.INDIVIDUAL}>Individual</MenuItem>
                <MenuItem value={USER_TYPE.DEALER}>Dealer</MenuItem>
              </Select>
              <FormHelperText error>
                {formik.touched.userType && typeof formik.errors.userType === 'string'
                  ? formik.errors.userType
                  : ''}
              </FormHelperText>
            </div>

            {formik.values.userType !== 'default' ? (
              <div className="control_group w_50">
                <label>Select User</label>
                <Select
                  fullWidth
                  id="selectedUser"
                  name="selectedUser"
                  value={userData?.length ? formik.values.selectedUser : ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.selectedUser && Boolean(formik.errors.selectedUser)}
                >
                  <MenuItem disabled value="default">
                    Select User
                  </MenuItem>
                  {userData?.length
                    ? userData?.map((item: any) => {
                        return (
                          <MenuItem
                            key={userData?.length ? item?._id : ''}
                            value={userData?.length ? item?._id : ''}
                          >
                            {item?.name || ''} {item?.email}
                          </MenuItem>
                        );
                      })
                    : null}
                </Select>
                <FormHelperText error>
                  {formik.touched.selectedUser && typeof formik.errors.selectedUser === 'string'
                    ? formik.errors.selectedUser
                    : ''}
                </FormHelperText>
              </div>
            ) : null}
            {formik.values.userType !== 'default' ? <div className="control_group w_50" /> : null}
            {selectedVehicleType === VEHICLE_TYPE.BIKE ? (
              <BikeForm
                formik={formik}
                masterData={masterData}
                features={features}
                fleetDamage={fleetDamage}
                setFeatures={setFeatures}
                setFleetDamage={setFleetDamage}
                carImages={carImages}
                setCarImages={setCarImages}
                coverImage={coverImage}
                setCoverImage={setCoverImage}
                accidentHistory={accidentHistory}
                setAccidentHistory={setAccidentHistory}
              />
            ) : selectedVehicleType === VEHICLE_TYPE.CAR ||
              selectedVehicleType === VEHICLE_TYPE.TRUCK ||
              selectedVehicleType === VEHICLE_TYPE.HEAVY_VEHICLE ? (
              <CarForm
                formik={formik}
                imageInfo={imageInfo}
                setImageInfo={setImageInfo}
                masterData={masterData}
                features={features}
                fleetDamage={fleetDamage}
                setFeatures={setFeatures}
                setFleetDamage={setFleetDamage}
                carImages={carImages}
                setCarImages={setCarImages}
                coverImage={coverImage}
                setCoverImage={setCoverImage}
                accidentHistory={accidentHistory}
                setAccidentHistory={setAccidentHistory}
              />
            ) : null}
          </div>

          <div className="form_end_btn">
            <Button className="btn btn_secondary" type="submit" disabled={isLoading}>
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddVehicle;
